import { useState } from "react";
import { useListStatements, useListReceipts, useListMatchFailures } from "@workspace/api-client-react";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { FileText, Receipt, CheckCircle, AlertCircle, Inbox, RefreshCw } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Dashboard() {
  const { data: statements } = useListStatements();
  const { data: receipts } = useListReceipts();
  const { data: failures } = useListMatchFailures({ reviewed: false });
  const { isRealAdmin } = useAuth();

  // Compute stats
  const totalStatements = statements?.length || 0;
  const totalEntries = statements?.reduce((acc, s) => acc + s.totalEntries, 0) || 0;
  const totalMatched = statements?.reduce((acc, s) => acc + s.matchedEntries, 0) || 0;
  const matchRate = totalEntries > 0 ? Math.round((totalMatched / totalEntries) * 100) : 0;
  const unmatchedEntries = totalEntries - totalMatched;
  const pendingFailures = failures?.length || 0;

  // Mock chart data for visual polish
  const chartData = [
    { name: "Mon", matched: 400, unmatched: 240 },
    { name: "Tue", matched: 300, unmatched: 139 },
    { name: "Wed", matched: 550, unmatched: 380 },
    { name: "Thu", matched: 450, unmatched: 190 },
    { name: "Fri", matched: 600, unmatched: 200 },
    { name: "Sat", matched: 700, unmatched: 100 },
    { name: "Sun", matched: 800, unmatched: 50 },
  ];

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">Overview of your expense matching process.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          title="Total Statements" 
          value={totalStatements.toString()} 
          icon={FileText} 
          trend="+2 this week"
        />
        <StatCard 
          title="Match Rate" 
          value={`${matchRate}%`} 
          icon={CheckCircle} 
          iconColor="text-green-500"
          trend={`${totalMatched} of ${totalEntries} entries`}
        />
        <StatCard 
          title="Unmatched Entries" 
          value={unmatchedEntries.toString()} 
          icon={Receipt} 
          iconColor="text-amber-500"
          trend="Requires action"
        />
        <StatCard 
          title="Review Queue" 
          value={pendingFailures.toString()} 
          icon={AlertCircle} 
          iconColor="text-red-500"
          trend="Failed auto-matches"
        />
      </div>

      {isRealAdmin && (
        <div className="mb-8">
          <EmailPollerCard />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="col-span-1 lg:col-span-2">
          <CardHeader>
            <CardTitle>Matching Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorMatched" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorUnmatched" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `£${v}`} />
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                  <Area type="monotone" dataKey="matched" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorMatched)" />
                  <Area type="monotone" dataKey="unmatched" stroke="#f59e0b" strokeWidth={2} fillOpacity={1} fill="url(#colorUnmatched)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Statements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {statements?.slice(0, 5).map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900 truncate max-w-[150px]">{s.originalFilename}</p>
                      <p className="text-xs text-slate-500">
                        {s.statementDate ? new Date(s.statementDate).toLocaleDateString() : 'Unknown date'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-900">{formatCurrency(s.totalSpending || 0)}</p>
                    <p className="text-xs text-slate-500">{s.matchedEntries}/{s.totalEntries} matched</p>
                  </div>
                </div>
              ))}
              {!statements?.length && (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No statements uploaded yet.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function EmailPollerCard() {
  const { toast } = useToast();
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState<{ processed: number; review: number; errors: string[] } | null>(null);

  const runPoll = async () => {
    if (running) return;
    setRunning(true);
    setLastResult(null);
    try {
      const res = await fetch("/api/settings/email-poller/run", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setLastResult({ processed: data.processed ?? 0, review: data.review ?? 0, errors: data.errors ?? [] });
      toast({
        title: "Inbox polled",
        description: `${data.processed ?? 0} new receipt(s), ${data.review ?? 0} sent to review`,
      });
    } catch (err: any) {
      toast({ title: "Poll failed", description: err?.message || "Unknown error", variant: "destructive" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
            <Inbox className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <CardTitle>Email Inbox Poller</CardTitle>
            <CardDescription>Manually fetch new receipts from the shared inbox.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Button onClick={runPoll} disabled={running} className="sm:w-auto w-full">
            <RefreshCw className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} />
            {running ? "Polling inbox..." : "Poll inbox now"}
          </Button>
          {lastResult && (
            <div className="text-sm text-slate-600 flex-1">
              <span className="font-medium text-slate-900">Last run:</span>{" "}
              {lastResult.processed} processed, {lastResult.review} sent to review
              {lastResult.errors.length > 0 && (
                <span className="text-rose-600">, {lastResult.errors.length} error(s)</span>
              )}
            </div>
          )}
        </div>
        {lastResult && lastResult.errors.length > 0 && (
          <ul className="mt-3 list-disc list-inside text-xs text-rose-600 space-y-0.5">
            {lastResult.errors.slice(0, 5).map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({ title, value, icon: Icon, trend, iconColor = "text-primary" }: any) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-display font-bold text-slate-900">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center ${iconColor}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        <div className="mt-4 text-sm text-slate-600">
          {trend}
        </div>
      </CardContent>
    </Card>
  );
}
